import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Select, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';

/**
 * Le catalogue des offres (migration 270) : chaque formule (Grimoire, pass, histoire, calendrier…) et
 * chaque valeur qu'on y choisit (tomes du Grimoire, éditions du calendrier) a son interrupteur et sa
 * FENÊTRE DE VENTE. « En vente » = l'interrupteur ouvert ET la date dans la fenêtre : le menu du
 * portail, le générateur public, les liens cadeau, la boutique du site et le paiement suivent. Le
 * Grimoire est en vente dès qu'une de ses formules l'est. Tout naît fermé (migrations 228, 270).
 */

const euros = (c) => `${(c / 100).toFixed(2).replace('.', ',')} €`;
const FORMAT = { pdf: 'PDF', imprime: 'imprimé' };
const LIVRABLE = {
  grimoire: (p) => `Grimoire — ${FORMAT[p.formatCahier] || '?'}`,
  acces: () => 'Pass',
  histoire: () => 'Histoire à l’unité',
  calendrier: () => 'Calendrier de l’Avent',
};
const GROUPES = {
  tome: { titre: 'Tomes du Grimoire', aide: 'Le tome que l’acheteur choisit dans le générateur. Un tome fermé n’est pas proposé ; un livre déjà payé garde son tome.' },
  edition_calendrier: { titre: 'Éditions du calendrier de l’Avent', aide: 'L’édition que l’acheteur choisit (compositions figées : config/calendrier/).' },
};

// La carte de la boutique du site (migration 271) ; vide = l'offre n'a pas de carte.
const VITRINES = [
  { valeur: 'masque', libelle: 'Masquée' },
  { valeur: 'bientot', libelle: 'Bientôt' },
  { valeur: 'vente', libelle: 'En vente' },
];

/** Le choix « Sur le site », et ce que le site montrera vraiment. */
function Vitrine({ x, vendable, disabled, onChange }) {
  if (!x.vitrine) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return (
    <Box>
      <Select size="small" value={x.vitrine} disabled={disabled} onChange={(e) => onChange(e.target.value)} inputProps={{ 'aria-label': `${x.titre || x.libelle} : carte du site` }}>
        {VITRINES.map((v) => <MenuItem key={v.valeur} value={v.valeur}>{v.libelle}</MenuItem>)}
      </Select>
      {x.vitrine === 'vente' && !vendable && (
        <Typography variant="caption" color="warning.main" display="block">Montrée « Bientôt » tant qu’elle n’est pas en vente</Typography>
      )}
    </Box>
  );
}

const dateFr = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null);
/** ISO → valeur d'un champ datetime-local (heure de Paris du navigateur). */
const versChamp = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const z = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
};
const depuisChamp = (v) => (v ? new Date(v).toISOString() : null);

function fenetre(x) {
  if (!x.venteDebut && !x.venteFin) return 'Toujours';
  if (x.venteDebut && x.venteFin) return `Du ${dateFr(x.venteDebut)} au ${dateFr(x.venteFin)}`;
  return x.venteDebut ? `À partir du ${dateFr(x.venteDebut)}` : `Jusqu’au ${dateFr(x.venteFin)}`;
}

/** L'état lisible : en vente, fermé, ou ouvert mais hors de sa fenêtre. */
function Etat({ x }) {
  if (x.enVente) return <Chip size="small" color="success" label="En vente" />;
  if (!x.actif) return <Chip size="small" label="Fermé" />;
  const avant = x.venteDebut && new Date(x.venteDebut) > new Date();
  return <Chip size="small" color="warning" label={avant ? `Ouvre le ${dateFr(x.venteDebut)}` : 'Vente terminée'} />;
}

/** La fiche d'une formule ou d'une valeur de choix : présentation, prix, fenêtre de vente. */
function Fiche({ cible, onFermer, onEnregistrer }) {
  const [champs, setChamps] = useState({});
  const [erreur, setErreur] = useState(null);
  useEffect(() => {
    if (!cible) return;
    const x = cible.ligne;
    setChamps({
      libelle: x.libelle || '', titre: x.titre || '', detail: x.detail || '',
      prix: x.prixCentimes != null ? (x.prixCentimes / 100).toFixed(2).replace('.', ',') : '',
      venteDebut: versChamp(x.venteDebut), venteFin: versChamp(x.venteFin),
    });
    setErreur(null);
  }, [cible]);
  if (!cible) return null;
  const offre = cible.type === 'offre';
  const x = cible.ligne;
  const changer = (cle) => (e) => setChamps((c) => ({ ...c, [cle]: e.target.value }));

  const enregistrer = async () => {
    const envoi = { venteDebut: depuisChamp(champs.venteDebut), venteFin: depuisChamp(champs.venteFin), detail: champs.detail };
    if (offre) {
      envoi.titre = champs.titre;
      if (!x.venduEnBoutique) {
        const prix = Math.round(Number(String(champs.prix).replace(',', '.')) * 100);
        if (!Number.isInteger(prix) || prix <= 0) return setErreur('Prix illisible.');
        if (prix !== x.prixCentimes) envoi.prixCentimes = prix;
      }
    } else {
      envoi.libelle = champs.libelle;
    }
    try {
      await onEnregistrer(envoi);
    } catch (e) {
      setErreur(e?.response?.data?.error || 'Enregistrement impossible');
    }
    return null;
  };

  return (
    <Dialog open onClose={onFermer} fullWidth maxWidth="sm">
      <DialogTitle>{offre ? x.libelle : `${GROUPES[x.groupe]?.titre || x.groupe} — ${x.libelle}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erreur && <Alert severity="error">{erreur}</Alert>}
          {offre ? (
            <>
              <TextField label="Titre affiché" value={champs.titre} onChange={changer('titre')} inputProps={{ maxLength: 80 }} helperText="Sur la carte de la formule (portail, boutique)." />
              <TextField
                label="Prix (€)"
                value={champs.prix}
                onChange={changer('prix')}
                disabled={x.venduEnBoutique}
                helperText={x.venduEnBoutique ? 'Vendu aussi dans l’App Store et Google Play : le prix se change là-bas.' : 'Le port de l’imprimé (3,90 €) s’ajoute à part.'}
              />
            </>
          ) : (
            <TextField label="Libellé" value={champs.libelle} onChange={changer('libelle')} inputProps={{ maxLength: 120 }} />
          )}
          <TextField label="Détail" value={champs.detail} onChange={changer('detail')} multiline minRows={2} inputProps={{ maxLength: 400 }} />
          <Stack direction="row" spacing={2}>
            <TextField label="Début de la vente" type="datetime-local" value={champs.venteDebut} onChange={changer('venteDebut')} InputLabelProps={{ shrink: true }} fullWidth helperText="Vide : déjà ouverte" />
            <TextField label="Fin de la vente" type="datetime-local" value={champs.venteFin} onChange={changer('venteFin')} InputLabelProps={{ shrink: true }} fullWidth helperText="Vide : sans fin (fin exclue)" />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            L’interrupteur reste maître : une formule fermée ne se vend pas, même dans sa fenêtre.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFermer}>Annuler</Button>
        <Button variant="contained" onClick={enregistrer}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function GrimoireScreen() {
  const [produits, setProduits] = useState(null);
  const [options, setOptions] = useState([]);
  // Cloudprinter prêt à imprimer ? (clés, mode Live en production) — sinon l'imprimé ne s'ouvre pas.
  const [impression, setImpression] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(null);
  const [fiche, setFiche] = useState(null);

  const charger = useCallback(async () => {
    try {
      const r = await AdminService.getProduitsPonctuels();
      setProduits(r.produits || []);
      setOptions(r.options || []);
      setImpression(r.impression || null);
      setErreur(null);
    } catch (e) {
      setErreur(e?.response?.data?.error || 'Lecture impossible');
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // Après chaque écriture, on relit tout : l'état « en vente » se calcule au serveur.
  const ecrire = async (cle, action) => {
    setEnCours(cle);
    try {
      await action();
      await charger();
    } catch (e) {
      setErreur(e?.response?.data?.error || 'Mise à jour impossible');
    } finally {
      setEnCours(null);
    }
  };

  const grimoireOuvert = (produits || []).some((p) => p.livrable === 'grimoire' && p.enVente);
  const groupes = [...new Set(options.map((o) => o.groupe))];

  const enregistrerFiche = async (envoi) => {
    const { type, ligne } = fiche;
    if (type === 'offre') await AdminService.modifierProduitPonctuel(ligne.code, envoi);
    else await AdminService.modifierOptionOffre(ligne.groupe, ligne.cle, envoi);
    setFiche(null);
    await charger();
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3, maxWidth: 1150 }}>
        <Typography variant="h5" gutterBottom>Offres : Grimoire, calendrier, pass et histoires</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Une offre se vend quand son interrupteur est ouvert ET que la date est dans sa fenêtre de vente.
          Le Grimoire apparaît aux parents (menu, « Nos offres ») et le générateur public s’ouvre dès qu’une
          de ses formules est en vente. Les tomes et les éditions se règlent de la même façon, plus bas.
          « Sur le site » règle la carte de la boutique du site : masquée, « Bientôt » (avec « Me prévenir »)
          ou en vente — achetable seulement quand l’offre est en vente.
        </Typography>
        {erreur && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErreur(null)}>{erreur}</Alert>}
        {produits && (
          <Alert severity={grimoireOuvert ? 'success' : 'info'} sx={{ mb: 2 }}>
            {grimoireOuvert ? 'Le Grimoire est EN VENTE.' : 'Le Grimoire est fermé : aucune de ses formules n’est en vente.'}
          </Alert>
        )}
        {impression && (
          <Alert severity={impression.pret ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {impression.pret
              ? `Impression chez Cloudprinter prête (mode ${impression.mode}) : les formules imprimées peuvent s’ouvrir. Suivi des commandes : « Grimoires imprimés ».`
              : `Les formules imprimées ne peuvent pas s’ouvrir : ${impression.raisons.join(' ')}`}
          </Alert>
        )}
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Formules" />
          <CardContent>
            {!produits ? <CircularProgress /> : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Formule</TableCell>
                      <TableCell>Livre</TableCell>
                      <TableCell align="right">Prix</TableCell>
                      <TableCell>Fenêtre de vente</TableCell>
                      <TableCell>État</TableCell>
                      <TableCell align="center">Ouverte</TableCell>
                      <TableCell>Sur le site</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {produits.map((p) => (
                      <TableRow key={p.code}>
                        <TableCell>
                          <Typography variant="body2">{p.titre || p.libelle}</Typography>
                          <Typography variant="caption" color="text.secondary">{p.code}{p.dureeJours ? ` · ${p.dureeJours} j d’aventure` : ''}</Typography>
                        </TableCell>
                        <TableCell><Chip size="small" variant="outlined" label={LIVRABLE[p.livrable] ? LIVRABLE[p.livrable](p) : p.livrable} /></TableCell>
                        <TableCell align="right">
                          {euros(p.prixCentimes)}
                          {p.formatCahier === 'imprime' ? <Typography variant="caption" color="text.secondary" display="block">+ port 3,90 €</Typography> : null}
                        </TableCell>
                        <TableCell><Typography variant="caption">{fenetre(p)}</Typography></TableCell>
                        <TableCell><Etat x={p} /></TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={p.actif}
                            disabled={enCours === p.code || (!p.actif && p.formatCahier === 'imprime' && impression && !impression.pret)}
                            onChange={() => ecrire(p.code, () => AdminService.basculerProduitPonctuel(p.code, !p.actif))}
                            inputProps={{ 'aria-label': `${p.libelle} : ${p.actif ? 'fermer' : 'ouvrir'}` }}
                          />
                        </TableCell>
                        <TableCell>
                          <Vitrine x={p} vendable={p.enVente} disabled={enCours === p.code} onChange={(v) => ecrire(p.code, () => AdminService.modifierProduitPonctuel(p.code, { vitrine: v }))} />
                        </TableCell>
                        <TableCell><Button size="small" onClick={() => setFiche({ type: 'offre', ligne: p })}>Modifier</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
        {groupes.map((g) => (
          <Card key={g} sx={{ mb: 3 }}>
            <CardHeader title={GROUPES[g]?.titre || g} subheader={GROUPES[g]?.aide} />
            <CardContent>
              {(() => {
                // Une valeur ne se vend que si une formule qui la propose est en vente : on le dit ici.
                const formules = (produits || []).filter((p) => (p.choix || []).includes(g));
                if (!formules.length || formules.some((p) => p.enVente)) return null;
                return (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {`Aucune formule qui propose ce choix n’est en vente (${formules.map((p) => p.titre || p.libelle).join(', ')}) : ouvrez-la dans le tableau « Formules », interrupteur ET dates.`}
                  </Alert>
                );
              })()}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Valeur</TableCell>
                      <TableCell>Fenêtre de vente</TableCell>
                      <TableCell>État</TableCell>
                      <TableCell align="center">Ouverte</TableCell>
                      <TableCell>Sur le site</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {options.filter((o) => o.groupe === g).map((o) => (
                      <TableRow key={o.cle}>
                        <TableCell>
                          <Typography variant="body2">{o.libelle}</Typography>
                          <Typography variant="caption" color="text.secondary">{o.cle}</Typography>
                        </TableCell>
                        <TableCell><Typography variant="caption">{fenetre(o)}</Typography></TableCell>
                        <TableCell><Etat x={o} /></TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={o.actif}
                            disabled={enCours === `${g}/${o.cle}`}
                            onChange={() => ecrire(`${g}/${o.cle}`, () => AdminService.modifierOptionOffre(g, o.cle, { actif: !o.actif }))}
                            inputProps={{ 'aria-label': `${o.libelle} : ${o.actif ? 'fermer' : 'ouvrir'}` }}
                          />
                        </TableCell>
                        <TableCell>
                          <Vitrine
                            x={o}
                            vendable={o.enVente && (produits || []).some((p) => p.enVente && (p.choix || []).includes(g))}
                            disabled={enCours === `${g}/${o.cle}`}
                            onChange={(v) => ecrire(`${g}/${o.cle}`, () => AdminService.modifierOptionOffre(g, o.cle, { vitrine: v }))}
                          />
                        </TableCell>
                        <TableCell><Button size="small" onClick={() => setFiche({ type: 'option', ligne: o })}>Modifier</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ))}
        <Fiche cible={fiche} onFermer={() => setFiche(null)} onEnregistrer={enregistrerFiche} />
      </Box>
    </AdminLayout>
  );
}
